import { createSelector } from "@reduxjs/toolkit";
import _ from "lodash";

import { AutoRegionRequest } from "@skydio/channels/src/client_autofocus_request_pb";
import { CaptureSettings } from "@skydio/channels/src/update_capture_settings_pb";
import { DockClientState } from "@skydio/pbtypes/pbtypes/gen/dock_client/dock_client_state_pb";
import { G47DockStateEnum } from "@skydio/pbtypes/pbtypes/gen/dock/g47_dock_state_enum_pb";
import { EncoderMode } from "@skydio/pbtypes/pbtypes/gen/user_camera/encoder_mode_pb";
import { RecordingMode } from "@skydio/pbtypes/pbtypes/gen/user_camera/recording_mode_pb";
import { RemoteDeviceType } from "@skydio/skybus/src/transport/types";

import type { ValueType } from "@skydio/core";
import type { AppState } from "state/store";
import type { ActiveAlertDetails, AlertType } from "./alertDefinitions";

export const getTeleopSessionId = (state: AppState) => state.teleop.teleopSessionId;

export const getRoomName = (state: AppState) => state.teleop.roomName;
export const getSkillsListRequest = (state: AppState) => state.teleop.skillsListRequest;
export const getUpdateCaptureSettingsRequestStatus = (state: AppState) =>
  state.teleop.captureSettingsRequest.status;
export const getCaptureSettings = (state: AppState) => state.teleop.captureSettings;
export const getIrCaptureSettings = (state: AppState) => state.teleop.irCaptureSettings;
export const getSubjectSettings = (state: AppState) => state.teleop.subjectSettings;
export const getZoomLevel = (state: AppState) => state.uiFeedback.zoomLevel;

export const getCaptureSettingsProto = createSelector(getCaptureSettings, settings => {
  const settingsProto = new CaptureSettings();
  if (!settings) return settingsProto;

  // NOTE(bojanin): this is required so that the camera_service doesn't reject your request.
  settingsProto.setStreamEncoderMode(EncoderMode.Enum.FORMAT_AVC);
  settingsProto.setValidationId(settings.validationId);
  settingsProto.setRecordingMode(settings.recordingMode);
  settingsProto.setVideoAutoRecord(settings.videoAutoRecord);
  settingsProto.setPhotoFormat(settings.photoFormat);
  settingsProto.setPhotoInterval(settings.photoInterval);
  settingsProto.setIso(settings.iso);
  settingsProto.setExposure(settings.exposure);
  settingsProto.setCompensation(settings.compensation);
  settingsProto.setWhiteBalance(settings.whiteBalance);
  settingsProto.setEncoderMode(settings.encoderMode);
  settingsProto.setColorMode(settings.colorMode);
  settingsProto.setUserBitratePreset(settings.userBitratePreset);
  settingsProto.setUserBitrateOverride(settings.userBitrateOverride);
  settingsProto.setAntiBandingMode(settings.antiBandingMode);
  settingsProto.setPhoto15mpEnabled(settings.photo15mpEnabled);
  settingsProto.setPublishThumbnail(settings.publishThumbnail);
  settingsProto.setThumbnailWidth(settings.thumbnailWidth);
  settingsProto.setRecordingFileFormat(settings.recordingFileFormat);

  // autofocus related fields
  settingsProto.setAutofocusMode(settings.autofocusMode);

  if (settings?.autofocusRegion) {
    const autoRegionRequestProtoForAutofocus = new AutoRegionRequest();
    autoRegionRequestProtoForAutofocus.setReference(settings.autofocusRegion.reference);
    autoRegionRequestProtoForAutofocus.setRegionList(settings.autofocusRegion.regionList);
    autoRegionRequestProtoForAutofocus.setUtime(settings.autofocusRegion.utime);
    settingsProto.setAutofocusRegion(autoRegionRequestProtoForAutofocus);
  }

  if (settings?.autoexposureRegion) {
    const autoRegionRequestProtoForAutoexposure = new AutoRegionRequest();
    autoRegionRequestProtoForAutoexposure.setReference(settings.autoexposureRegion.reference);
    autoRegionRequestProtoForAutoexposure.setRegionList(settings.autoexposureRegion.regionList);
    autoRegionRequestProtoForAutoexposure.setUtime(settings.autoexposureRegion.utime);
    settingsProto.setAutoexposureRegion(autoRegionRequestProtoForAutoexposure);
  }

  return settingsProto;
});
export const hasCaptureSettings = (state: AppState) => !!state.teleop.captureSettings;
export const getRecordingMode = createSelector(
  getCaptureSettings,
  settings => settings?.recordingMode
);

const getRecordingModeKey = (recordingMode: ValueType<RecordingMode.EnumMap>) =>
  _.findKey(RecordingMode.Enum, o => o === recordingMode)!;

export const isInPhotoMode = createSelector(
  getRecordingMode,
  mode => mode !== undefined && getRecordingModeKey(mode).startsWith("PHOTO")
);
export const getSkybusPilotRequest = (
  state: AppState,
  deviceType: RemoteDeviceType = RemoteDeviceType.VEHICLE
) => {
  if (deviceType === RemoteDeviceType.DOCK) {
    return state.teleop.pilotRequest.skybusDock;
  }
  return state.teleop.pilotRequest.skybus;
};
export const getSkybusPilotRequestRetriesLeft = (
  state: AppState,
  deviceType: RemoteDeviceType = RemoteDeviceType.VEHICLE
) => {
  if (deviceType === RemoteDeviceType.DOCK) {
    return state.teleop.pilotRequest.skybusDockRetriesLeft;
  }
  return state.teleop.pilotRequest.skybusRetriesLeft;
};
export const getFlightDeckPilotRequest = (state: AppState) => state.teleop.pilotRequest.flightDeck;
export const getWantsAutoPrep = (state: AppState) => state.teleop.wantsAutoPrep;
export const getSkillOverride = (state: AppState) => state.teleop.skillOverride;
export const getLatestQcaStats = (state: AppState) => state.teleop.qcaStats;
export const getLatestQcaStatsLastUpdated = (state: AppState) => state.teleop.qcaStatsLastUpdated;
export const getLatestRelocalizationInfo = (state: AppState) => state.teleop.relocalizationInfo;
export const getDockClientStatus = (state: AppState) => state.teleop.dockClientStatus;

export const isDockLanding = createSelector(
  getDockClientStatus,
  dockClientStatus =>
    dockClientStatus &&
    dockClientStatus.isBasestationLandingSurface &&
    dockClientStatus.canReturnToDock
);
export const isDockLandingSurface = createSelector(
  getDockClientStatus,
  dockClientStatus => dockClientStatus?.isBasestationLandingSurface
);

export const getVehicleSid = (state: AppState) => state.teleop.vehicleSid;
export const getIsVehicleInRoom = (state: AppState) => state.teleop.vehicleInRoom;
export const getDockSid = (state: AppState) => state.teleop.dockSid;
export const getIsDockInRoom = (state: AppState) => state.teleop.dockInRoom;
export const getDockCameraStatus = (state: AppState) => state.teleop.dockCameraStatus;
export const getG47MotorState = (state: AppState) => state.teleop.g47MotorState;
export const getG47LedState = (state: AppState) => state.teleop.g47LedState;
export const getGroundhawkG47MotorStatuses = (state: AppState) =>
  state.teleop.groundhawkG47MotorStatuses;
export const isPilotForDock = (state: AppState) =>
  state.teleop.pilotRequest.skybusDock.status === "success";

export const getLatestWeatherStationData = (state: AppState) => state.teleop.weatherStationData;
export const getInferredWeatherStatus = (state: AppState) => state.teleop.inferredWeatherStatus;
export const getLatestWeatherStationDataLastUpdated = (state: AppState) =>
  state.teleop.weatherStationDataLastUpdated;

export const isDockInInspectionMode = createSelector(
  getDockClientStatus,
  dockClientStatus => dockClientStatus?.state === DockClientState.Enum.INSPECTION_MODE
);

export const getDockSliderPosition = createSelector(
  getDockClientStatus,
  dockClientStatus => dockClientStatus?.lastDockStatus?.dockSliderNormalizedPosition
);

export const canDockEnterInspectionMode = createSelector(
  getDockClientStatus,
  dockClientStatus => dockClientStatus?.canEnterInspectionMode
);

// Even in this case, there is a chance that the drone crashed into the dock or landed poorly
// (had a "bad landing"), and this is not a guarantee that the drone will be able to charge
export const hasLandedInDock = createSelector(
  getDockClientStatus,
  dockClientStatus =>
    dockClientStatus?.state === DockClientState.Enum.LANDED_IN_DOCK ||
    dockClientStatus?.state === DockClientState.Enum.IN_DOCK
);

export const isG47RoofTransitioning = createSelector(
  getG47MotorState,
  g47MotorState => g47MotorState?.dockState === G47DockStateEnum.Enum.TRANSITIONING
);

export const getG47RoofPosition = createSelector(
  getGroundhawkG47MotorStatuses,
  groundhawkG47MotorStatuses => groundhawkG47MotorStatuses?.portRoof?.normalizedPosition
);

export const getLTEStats = (state: AppState) => state.teleop.lteStats;

export const isADSBAlertActiveAndMuted = (state: AppState) =>
  !!state.teleop.activeAlerts["ADSB_TRAFFIC"]?.mutedAt;

/**
 * Returns a list of the active alerts ordered by priority containing the alert's details
 * @param state
 * @returns
 */
export const getAllActiveAlertDetails = (state: AppState): ActiveAlertDetails[] => {
  const alertTypes = state.teleop.alertConfigs;
  return Object.entries(state.teleop.activeAlerts)
    .filter(entry => entry[1] !== undefined)
    .map(([id, alert]) => ({
      ...alertTypes[id as AlertType](alert!.alertVariant),
      ...alert!,
    }))
    .sort((a, b) => a.priority - b.priority);
};

/**
 * Returns a list of the audios that have to be played for the active alerts
 * @param state
 * @returns
 */
export const getAudioQueue = (state: AppState): string[] => {
  const alertTypes = state.teleop.alertConfigs;
  const allActiveAlertDetails = Object.entries(state.teleop.activeAlerts)
    .filter(entry => entry[1] !== undefined)
    .map(([id, alert]) => ({
      ...alertTypes[id as AlertType](alert!.alertVariant),
      ...alert!,
    }));
  const alertsWithPendingAudio = allActiveAlertDetails.filter(
    alert => alert.audio && (!alert.audioPlayedAt || (alert.audio.repeat && alert.mutedAt == null))
  );
  const alertsWithPendingAudioByPriority = alertsWithPendingAudio.sort(
    (a, b) => a.priority - b.priority
  );
  const audioQueue = alertsWithPendingAudioByPriority.map(alert => alert.audio!.path);
  return audioQueue;
};

/**
 * Returns a boolean indicating if the alert manager display is active
 * @param state
 * @returns boolean
 */
export const getIsAlertManagerDisplayActive = (state: AppState): boolean => {
  return state.teleop.isAlertManagerDisplayActive;
};

export const getShowControlsModal = (state: AppState) => state.teleop.showControlsModal;

export const getShowSettingsModal = (state: AppState) => state.teleop.showSettingsModal;

export const getSettingsModalActiveTab = (state: AppState) => state.teleop.settingsModalActiveTab;

export const getSettingsPreviewDismissed = (state: AppState) =>
  state.teleop.settingsPreviewDismissed;

export const getVehicleWifiStatus = (state: AppState) => state.teleop.vehicleWifiStatus;

const getTeleopState = (state: AppState) => state.teleop;

// Refactor `getSelectedEntity` using `createSelector`
export const getSelectedEntity = createSelector(
  [getTeleopState],
  teleopState => teleopState.selectedEntity
);

export const getHasTriedToRequestPilot = (state: AppState) =>
  state.teleop.pilotRequest.hasTriedToRequestPilot;

export const getCommandeer = (state: AppState) => state.teleop.pilotRequest.commandeer;

export const getReportedPageNavigationType = (state: AppState) =>
  state.teleop.reportedPageNavigationType;

export const getShowMarkersSidebar = (state: AppState) => state.teleop.showMarkersSidebar;
export const getShowPerFlightFeedback = (state: AppState) => state.teleop.showPerFlightFeedback;
export const getLatestDeviceNodePingMessage = (state: AppState) =>
  state.teleop.latestDeviceNodePingMessage;
export const getLastDeviceNodeRTTTimeMs = (state: AppState) =>
  state.teleop.lastTenDeviceNodeRTTs[state.teleop.lastTenDeviceNodeRTTs.length - 1];
export const getDeviceNodeRTTLastTenAverageMs = (state: AppState) =>
  state.teleop.lastTenDeviceNodeRTTs.reduce((a, b) => a + b, 0) /
  state.teleop.lastTenDeviceNodeRTTs.length;

export const getShowParachuteModal = (state: AppState) => state.teleop.showParachuteModal;
export const getShowDropperModal = (state: AppState) => state.teleop.showDropperModal;
export const getDropperDropped = (state: AppState) => state.teleop.dropperDropped;
export const getDropperDropping = (state: AppState) => state.teleop.dropperDropping;
export const getNumParachutePresses = (state: AppState) => state.teleop.numParachutePresses;
export const getAllowChannelConflictAlert = (state: AppState) =>
  state.teleop.allowChannelConflictAlert;
export const getHasChannelConflict = (state: AppState) => state.teleop.hasChannelConflict;
export const getCurrentOtherUsedSiteChannels = (state: AppState) =>
  state.teleop.currentOtherUsedSiteChannels;
export const getShowPostFlightReport = (state: AppState) => state.teleop.showPostFlightReport;
export const getCockpitFocused = (state: AppState) => state.teleop.isCockpitFocused;
export const getShowTurtleModeModal = (state: AppState) => state.teleop.showTurtleModeModal;
export const hasPendingInspectionRequest = (state: AppState) => state.teleop.hasPendingInspection;
export const getLaunchLoading = (state: AppState) => state.teleop.launchLoading;
export const getLaunchWhenReady = (state: AppState) => state.teleop.launchWhenReady;
export const getLaunchButtonInteractable = (state: AppState) =>
  state.teleop.launchButtonInteractable;
